import { Typography, Tooltip, Collapse } from 'antd/es';
import { ReactElement, useMemo, useState } from 'react';
import { format, getYear, parseISO, addYears } from 'date-fns';
import i18n from 'i18next';
import { Link } from 'react-router-dom';
import { Button, Radio, Upload, UploadFile } from 'antd';
import {Trans, useTranslation} from 'react-i18next';
import { RcFile } from 'antd/es/upload';
import classNames from 'classnames';

import { SettingsCard } from './SettingsCard';
import { SettingsDisabledInput } from './SettingsDisabledInput';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import Utils from 'utils/utils';
import { Paths, TaxFormStatus, ClientRole } from 'utils/constants';
import { language } from 'utils';

import { DoubleTaxationItem, UserSettings } from 'models';

import { uploadTaxDocuments } from 'features/userSettings/actions';

import { ReactComponent as Close } from 'assets/icons/close.svg';
import { ReactComponent as AttachIcon } from 'assets/icons/attach.svg';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-select.svg';

import { selectCountries, selectUser, selectUserSettings } from 'selectors';

import s from './index.module.scss';
import upload from './upload.module.scss';
import { isBetweenDate } from '../../../utils/DateTime';

const MAX_COUNT_FILES = 4;
const MAX_SIZE = 10485760;

const WORDS = [
	'taxResidencyText',
	'countryResidencyText',
	'yourTaxRateText',
	'dasFormText',
	'readMoreResidencyText',
	'taxResidencyNotSpecifiedText',
	'specifyNowText',
	'errorBigFileText',
	'attachText',
	'sendDocumentsText',
	'statusDocumentsText',
	'errorMaxFilesText',
	'pIdSettingsText',
	'dasFormTitle',
];

const TAX_FORM_STATUSES: Record<string, string> = {
	PENDING: i18n.t('underConsiderationText'),
	APPROVED: i18n.t('approvedText'),
	REJECTED: i18n.t('rejectedText'),
	EXPIRED: i18n.t('expiredText'),
};

const LT = 'LT';

const getContent = (item: DoubleTaxationItem, reason: string) => {
	let content = reason
	if (item.status === TaxFormStatus.PENDING) {
		content = 'pendingDasStatusText';
	}
	return content;
};

export const SettingsResidencyCard = () => {
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [forFuture, setForFuture] = useState<number>(0);
	const t = useTranslate(WORDS);
	const tr = useTranslation();

	const dispatch = useAppDispatch();

	const countries = useAppSelector(selectCountries);
	const userSettings = useAppSelector(selectUserSettings);
	const user = useAppSelector(selectUser);

	const [isLoading, setIsLoading] = useState(false);

	const [error, setError] = useState('');

	const country = useMemo(
		() =>
			countries?.find((item: any) => item.iso === userSettings.tax_residence?.country?.iso)
				?.title || '',
		[countries, userSettings],
	);

	const beforeUpload = (file: RcFile, files: RcFile[]) => {
		if (error) {
			setError('');
		}

		const uploadedFiles = files
			.map((item) => {
				if (item.size > MAX_SIZE) {
					setError(t.errorBigFileText);
				}

				return item;
			})
			.filter((item) => item?.size < MAX_SIZE);

		const newFileList = [...fileList, ...uploadedFiles];

		if (newFileList.length > MAX_COUNT_FILES) {
			setError(t.errorMaxFilesText);
		}

		setFileList(newFileList);

		return false;
	};
	const onUploadFiles = () => {
		const formData = new FormData();

		formData.append('forFuture', forFuture.toString());

		fileList.forEach((item, i) => {
			return formData.append(`files[${i}]`, item as RcFile);
		});

		if (fileList) {
			setIsLoading(true);
			dispatch(uploadTaxDocuments(formData))
				.unwrap()
				.catch((errors: any) => {
					setError(i18n.t(errors.errors?.files ? errors.errors.files[0] : errors.message));
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const docStatus = userSettings.tax_residence?.double_taxation
		? userSettings.tax_residence.double_taxation[0].status
		: '';

	const isShowDasForm =
		userSettings.tax_residence?.country.iso !== LT &&
		userSettings.tax_residence?.double_taxation_avoidance === true &&
		user.userType === ClientRole.USER &&
		docStatus !== TaxFormStatus.PENDING;

	const renderRateText = (userSettings: UserSettings) => {
		return (
			<div className={s.rateText}>
				{t.yourTaxRateText}
				<Typography.Text type="danger">
					&nbsp;
					{userSettings.tax_residence?.tax?.rate &&
					Number.isInteger(+userSettings.tax_residence.tax?.rate)
						? Math.trunc(+userSettings.tax_residence.tax?.rate)
						: userSettings.tax_residence?.tax?.rate}
					%
				</Typography.Text>
			</div>
		);
	};

	const isEn = ['en','ro'].includes(language);

	return (
		<SettingsCard title={t.taxResidencyText}>
			{userSettings.tax_residence ? (
				<>
					<div className={s.rateGroup}>
						<div className={s.rateRow}>
							<SettingsDisabledInput
								className={classNames(s.col, s.inputMaxWith312)}
								name={t.countryResidencyText}
								value={country}
							/>
							<div className={s.rateWrapperDesktop}>{renderRateText(userSettings)}</div>
						</div>
						<div className={s.rateRow}>
							<SettingsDisabledInput
								className={classNames(s.col, s.inputMaxWith312)}
								name={t.pIdSettingsText}
								value={userSettings.tax_residence.identification_number}
							/>
						</div>
						<div className={`${s.rateRow} ${s.rateWrapperMobile}`}>
							{renderRateText(userSettings)}
						</div>
					</div>
					{isShowDasForm && (
						<>
							<p className={s.statusText}>{t.dasFormTitle}</p>
							<div className={s.uploadRow}>
								<div className={upload.root}>
									<Typography.Text className={upload.text}>{t.dasFormText}</Typography.Text>
									<label htmlFor="file" className={upload.label}>
										<Upload
											accept="image/jpeg,image/jpg,image/png,application/pdf"
											className={upload.upload}
											multiple
											name="file"
											listType="text"
											showUploadList={{
												showRemoveIcon: true,
												showDownloadIcon: false,
												showPreviewIcon: false,
											}}
											onRemove={(file) => {
												const index = fileList.indexOf(file);
												const newFileList = fileList.slice();
												newFileList.splice(index, 1);
												setFileList(newFileList);
												
												if (newFileList.length <= MAX_COUNT_FILES &&
													error === t.errorMaxFilesText) {
													setError('');
												}
											}}
											beforeUpload={beforeUpload}
											fileList={fileList}
											itemRender={(
												originNode: ReactElement,
												file: UploadFile,
												fileList: object[],
												actions: { remove: any },
											) => {
												return (
													<div key={file.name} className={`filter ${upload.filter}`}>
														<span className={upload.filterText}>{file.name}</span>
														<Button type="link" icon={<Close />} onClick={actions.remove} />
													</div>
												);
											}}
										>
											<Tooltip
												placement="topRight"
												overlayClassName="popover"
												title={
													<Trans
														i18nKey="filePDFText"
														components={{
															list_item: <li />,
															list: (
																<ul className={upload.list} onClick={(e) => e.stopPropagation()} />
															),
															das_link: (
																<a
																	className={upload.link}
																	href={Utils.getLinkToWP(
																		'/wp-content/uploads/2023/12/DAS-1-FR0021.pdf',
																		true,
																	)}
																	target="_blank"
																	rel="noreferrer"
																/>
															),
														}}
													/>
												}
											>
												<Button className={upload.button}>
													<AttachIcon className={upload.attach} />
													{t.attachText}&nbsp;
												</Button>
											</Tooltip>
										</Upload>

										{error && (
											<Typography.Text
												className={classNames(upload.error, {
													[upload.errorEmpty]: fileList.length === 0,
												})}
												type="danger"
											>
												{tr.t(error)}
											</Typography.Text>
										)}
									</label>
								</div>

								<div className={s.form}>
									<Radio.Group
										value={forFuture}
										onChange={(values) => {
											setForFuture(values.target.value);
											setFileList([]);
											setError('');
										}}
									>
										<Radio value={0} className={s.formItem}>
											{tr.t('currentYearText')}&nbsp;
											<span className={s.lowerCase}>
												(
												{tr.t('validPeriodTextForDoubleTaxation', {
													since: format(new Date(), 'yyyy-MM-dd'),
													until: format(new Date(), 'yyyy-12-31'),
												})}
												)
											</span>
										</Radio>
										{isBetweenDate({
											from: { month: 11, day: 20 },
											to: { month: 11, day: 31 },
										}) && (
											<Radio value={1} className={s.formItem}>
												{tr.t('nextYearText')}&nbsp;
												<span className={s.lowerCase}>
													(
													{tr.t('validPeriodTextForDoubleTaxation', {
														since: format(addYears(new Date(), 1), 'yyyy-01-01'),
														until: format(addYears(new Date(), 1), 'yyyy-12-31'),
													})}
													)
												</span>
											</Radio>
										)}
									</Radio.Group>
									{fileList.length > 0 && (
										<Button
											type="primary"
											className={s.sendDas}
											onClick={onUploadFiles}
											loading={isLoading}
											disabled={fileList.length > MAX_COUNT_FILES}
										>
											{t.sendDocumentsText}
										</Button>
									)}
								</div>
							</div>
						</>
					)}
					{userSettings.tax_residence.double_taxation && (
						<div className={s.doubleTaxation}>
							<Typography.Text className={s.statusText}>{t.statusDocumentsText}:</Typography.Text>

							<Collapse
								className={s.doubleTaxationCollapse}
								defaultActiveKey={Array.from(
									new Set(
										userSettings.tax_residence.double_taxation
											.map((item) => {
												const date = new Date(item.valid_since?.replace(/-/g, '/') || new Date());
												return getYear(date).toString();
											})
											.filter((_, i) => i === 0),
									),
								)}
								expandIconPosition="end"
								expandIcon={({ isActive }) => (
									<div
										className={classNames(s.collapseArrow, { [s.collapseArrowActive]: isActive })}
									>
										<span>
											<ArrowIcon />
										</span>
									</div>
								)}
								ghost
							>
								{Object.entries(
									userSettings.tax_residence.double_taxation.reduce((acc, item) => {
										const date = parseISO(item.valid_since);
										const year = getYear(date).toString();
										if (!acc[year]) acc[year] = [];
										acc[year].push(item);
										return acc;
									}, {} as Record<string, typeof userSettings.tax_residence.double_taxation>),
								)
									.sort(([a], [b]) => Number(b) - Number(a))
									.map(([year, yearItems]) => (
										<Collapse.Panel
											key={year}
											header={<span className={s.doubleTaxationYear}>{year}</span>}
										>
											<div className={s.doubleTaxationList}>
												{yearItems.map((item, i) => {
													const status = item.status;
													const content = getContent(item, item.reject_reason);
													return (
														<div className={s.doubleTaxationRow} key={i}>
															<div className={s.doubleTaxationInfo}>
																<span className={s.doubleTaxationPeriod}>
																	{tr.t('validPeriodTextForDoubleTaxation', {
																		since: format(parseISO(item.valid_since), 'yyyy-MM-dd'),
																		until: format(parseISO(item.valid_until), 'yyyy-MM-dd'),
																	})}
																</span>
																{content && (
																	<span className={s.doubleTaxationReason}>
																		<Trans i18nKey={content} />
																	</span>
																)}
															</div>
															<div
																className={classNames(s.attachedStatus, {
																	[s.red]: status === TaxFormStatus.REJECTED,
																	[s.green]: status === TaxFormStatus.APPROVED,
																	[s.blue]: status === TaxFormStatus.EXPIRED,
																	[s.gray]: status === TaxFormStatus.PENDING,
																})}
															>
																{TAX_FORM_STATUSES[status]}
															</div>
														</div>
													);
												})}
											</div>
										</Collapse.Panel>
									))}
							</Collapse>
						</div>
					)}{' '}
				</>
			) : (
				<div className={s.doubleTaxationRow}>
					<Typography.Text>{t.taxResidencyNotSpecifiedText}</Typography.Text>
					<Button className={s.specifyNow}>
						<Link to={Paths.RESIDENCY} state={{ fromOverview: true }}>
							{t.specifyNowText}
						</Link>
					</Button>
				</div>
			)}

			<Typography.Text type="secondary" className={s.readMore}>
				<a
					className={s.link}
					href={`https://crowdpear.com/${
						isEn ? 'help-category/taxes/' : 'lt/duk-category/mokesciai/'
					}`}
					target="_blank"
					rel="noreferrer"
				>
					{t.readMoreResidencyText}
				</a>
			</Typography.Text>
		</SettingsCard>
	);
};
